import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import { TALENT_POOL_KEY } from "@/api/query-keys";
import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import { toSubcategoryCsv } from "@/domains/company/active-search/utils/subcategory-filter";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { buildMatchPoolParams, matchQueryFromFilters } from "./match-params";
import { normalizeMembershipResponse } from "./membership";
import type {
  CapabilitiesResponse,
  ListPoolQuery,
  ListPoolResponse,
  MatchPoolQuery,
  MatchPoolResponse,
  MembershipLookupRequest,
  MembershipLookupResponse,
  SaveContactRequest,
  SaveContactResponse,
  UpdateContactRequest,
  UpdateContactResponse,
} from "./types";

const TALENT_POOL_BASE = "/v1/talent-pool";
const SEMANTIC_MATCH_TIMEOUT_MS = 30_000;

// Re-export the pure membership normalizer so it stays part of the api barrel.
export { normalizeMembershipResponse } from "./membership";

// ============================================
// Capabilities (GET /v1/talent-pool/capabilities) — non gated
// ============================================

// 1. Base fetcher
export const getTalentPoolCapabilities = async () => {
  const { data } = await axiosInstance.get<CapabilitiesResponse>(
    `${TALENT_POOL_BASE}/capabilities`,
  );
  return data;
};

// 2. Query config creator — keyed per workspace so switching company re-fetches
export const createCapabilitiesQueryConfig = (
  companyId: string | undefined,
) => ({
  queryKey: [TALENT_POOL_KEY, "capabilities", companyId],
  queryFn: () => getTalentPoolCapabilities(),
  staleTime: 5 * 60 * 1000,
});

// 3. Custom hook
export const useTalentPoolCapabilities = () => {
  const [store] = useCompanyStore();
  return useQuery(() => {
    const companyId = store.selectedCompany?.id;
    return {
      ...createCapabilitiesQueryConfig(companyId),
      enabled: !!companyId,
    };
  });
};

// ============================================
// Membership lookup (POST /v1/talent-pool/membership) — non gated, read-only
// ============================================

// 1. Base fetcher — returns the freelancer-profile-id → membership map (unwrapped)
export const lookupTalentPoolMembership = async (ids: string[]) => {
  const body: MembershipLookupRequest = { freelancer_profile_ids: ids };
  const { data } = await axiosInstance.post<MembershipLookupResponse>(
    `${TALENT_POOL_BASE}/membership`,
    body,
  );
  return normalizeMembershipResponse(data);
};

// 2. Query config creator — keyed per workspace + requested ids
export const createMembershipQueryConfig = (
  companyId: string | undefined,
  ids: string[],
) => ({
  queryKey: [TALENT_POOL_KEY, "membership", companyId, ids],
  queryFn: () => lookupTalentPoolMembership(ids),
});

// ============================================
// Save (POST /v1/talent-pool)
// ============================================

// 1. Base mutator
export const saveTalentPoolFreelancer = async (input: SaveContactRequest) => {
  const { data } = await axiosInstance.post<SaveContactResponse>(
    TALENT_POOL_BASE,
    input,
  );
  return data;
};

// 2. Raw mutation hook — no toast, no tracking (orchestration lives in T4's provider)
export const useSaveFreelancerMutation = () =>
  useMutation(() => ({
    mutationFn: (input: SaveContactRequest) => saveTalentPoolFreelancer(input),
  }));

// ============================================
// Update (PATCH /v1/talent-pool/:freelancerId) — :freelancerId is the FREELANCER PROFILE id
// ============================================

// 1. Base mutator
export const updateTalentPoolFreelancer = async (
  freelancerProfileId: string,
  input: UpdateContactRequest,
) => {
  const { data } = await axiosInstance.patch<UpdateContactResponse>(
    `${TALENT_POOL_BASE}/${freelancerProfileId}`,
    input,
  );
  return data;
};

// 2. Raw mutation hook — no toast, no tracking
export const useUpdateSavedFreelancerMutation = () =>
  useMutation(() => ({
    mutationFn: (variables: {
      freelancerProfileId: string;
      input: UpdateContactRequest;
    }) =>
      updateTalentPoolFreelancer(
        variables.freelancerProfileId,
        variables.input,
      ),
  }));

// ============================================
// Remove (DELETE /v1/talent-pool/:freelancerId) — idempotent, 204 no body
// ============================================

// 1. Base mutator
export const removeTalentPoolFreelancer = async (
  freelancerProfileId: string,
) => {
  await axiosInstance.delete(`${TALENT_POOL_BASE}/${freelancerProfileId}`);
};

// 2. Raw mutation hook — no toast, no tracking
export const useRemoveSavedFreelancerMutation = () =>
  useMutation(() => ({
    mutationFn: (freelancerProfileId: string) =>
      removeTalentPoolFreelancer(freelancerProfileId),
  }));

// ============================================
// List (GET /v1/talent-pool) — paginated + filterable
// ============================================

// Serialized query params handed to axios (which URL-encodes them; a comma → %2C).
type SerializedListParams = Record<string, string | number>;

// Query-key segments that may differ while still bridging previous data
// (page & sort changes keep the old page visible; a filter/company change must not).
const NON_FILTER_KEY_SEGMENTS = new Set(["page", "sortBy", "sortOrder"]);

// Pure param builder — CSV-joins the skills array and drops every undefined / null /
// empty-string / empty-array param (ATS "defaults omitted" idiom). Axios-free and
// exported so it stays unit-testable in the node env.
export const buildListPoolParams = (
  query: ListPoolQuery,
): SerializedListParams => {
  const params: SerializedListParams = {};

  const add = (key: string, value: string | number | null | undefined) => {
    if (value === undefined || value === null || value === "") return;
    params[key] = value;
  };

  add("page", query.page);
  add("per_page", query.per_page);
  add("sortBy", query.sortBy);
  add("sortOrder", query.sortOrder);
  add("search", query.search);
  add("job_category", query.job_category);
  if (query.job_category) {
    add("job_subcategory", toSubcategoryCsv(query.job_subcategory));
  }
  add("work_preference", query.work_preference);
  add("price_per_day_min", query.price_per_day_min);
  add("price_per_day_max", query.price_per_day_max);
  add("weekly_hours", query.weekly_hours);
  add("available_from", query.available_from);

  if (query.skills && query.skills.length > 0) {
    params.skills = query.skills.join(",");
  }

  return params;
};

// Keeps only the filter segments of a serialized-params object (excludes page/sort).
const filterSegments = (params: SerializedListParams): SerializedListParams => {
  const out: SerializedListParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (!NON_FILTER_KEY_SEGMENTS.has(key)) out[key] = value;
  }
  return out;
};

// Shallow equality over the filter segments only.
const sameFilterSegments = (
  a: SerializedListParams,
  b: SerializedListParams,
): boolean => {
  const fa = filterSegments(a);
  const fb = filterSegments(b);
  const keysA = Object.keys(fa);
  if (keysA.length !== Object.keys(fb).length) return false;
  return keysA.every((key) => fa[key] === fb[key]);
};

// Decides whether the previous list page's data may be shown as placeholder for the
// incoming request. Bridges ONLY within the same workspace AND the same filter set —
// page/sort changes may bridge, a company or filter change must NOT (otherwise a
// workspace switch would flash another workspace's rows — adversarial-review finding).
// Deliberately NOT the bare `keepPreviousData` re-export, which bridges across ANY key.
export const shouldBridgePoolListData = (
  previousKey: readonly unknown[] | undefined,
  nextCompanyId: string | undefined,
  nextParams: SerializedListParams,
): boolean => {
  if (!previousKey) return false;
  const prevCompanyId = previousKey[2] as string | undefined;
  const prevParams = (previousKey[3] ?? {}) as SerializedListParams;
  if (prevCompanyId !== nextCompanyId) return false;
  return sameFilterSegments(prevParams, nextParams);
};

// 1. Base fetcher
export const listTalentPool = async (query: ListPoolQuery = {}) => {
  const { data } = await axiosInstance.get<ListPoolResponse>(TALENT_POOL_BASE, {
    params: buildListPoolParams(query),
  });
  return data;
};

// 2. Query config creator — keyed per workspace + serialized params so switching
// company or any filter/sort/page yields a distinct cache entry.
export const createPoolListQueryConfig = (
  companyId: string | undefined,
  query: ListPoolQuery,
) => {
  const serializedParams = buildListPoolParams(query);
  return {
    queryKey: [TALENT_POOL_KEY, "list", companyId, serializedParams] as const,
    queryFn: () => listTalentPool(query),
    // Function form (NOT `keepPreviousData`): keep the previous page only when it
    // belongs to the same workspace + filter set. See `shouldBridgePoolListData`.
    placeholderData: (
      previousData: ListPoolResponse | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined,
    ): ListPoolResponse | undefined =>
      shouldBridgePoolListData(
        previousQuery?.queryKey,
        companyId,
        serializedParams,
      )
        ? previousData
        : undefined,
  };
};

// 3. Custom hook — the `enabled` gate (`!!companyId && capabilitiesEnabled`) is the
// caller's responsibility; it is threaded through the accessor so the list never
// fetches while capabilities are unresolved or the plan is locked.
export const usePoolList = (
  accessor: () => {
    companyId: string | undefined;
    query: ListPoolQuery;
    enabled: boolean;
  },
) =>
  useQuery(() => {
    const { companyId, query, enabled } = accessor();
    return {
      ...createPoolListQueryConfig(companyId, query),
      enabled,
    };
  });

// ============================================
// Match (GET /v1/talent-pool/match) — search-contract twin, paginated
// ============================================

// Re-export the pure request builders so they stay part of the api barrel.
export {
  buildMatchPoolParams,
  DEFAULT_MATCH_PER_PAGE,
  matchQueryFromFilters,
} from "./match-params";

// 1. Base fetcher — GET with serialized query params (no body).
export const getTalentPoolMatch = async (
  query: MatchPoolQuery,
  signal?: AbortSignal,
) => {
  const { data } = await axiosInstance.get<MatchPoolResponse>(
    `${TALENT_POOL_BASE}/match`,
    {
      params: buildMatchPoolParams(query),
      signal,
      timeout: SEMANTIC_MATCH_TIMEOUT_MS,
    },
  );
  return data;
};

// 2. Infinite-query config creator. Key (D15): the workspace, the offer, and a
// filter fingerprint (page excluded — it is the pageParam). A workspace switch or
// any filter change re-keys and resets to page 1; page/scroll changes stay in the
// same cache entry. Mirrors `useInfiniteSearchFreelancers`' accessor-keyed shape.
export const shouldBridgePoolMatchData = (
  previousKey: readonly unknown[] | undefined,
  nextCompanyId: string | undefined,
  nextJobOfferId: string,
): boolean =>
  previousKey?.[2] === nextCompanyId && previousKey?.[3] === nextJobOfferId;

export const createMatchQueryConfig = (
  companyId: string | undefined,
  jobOfferId: string,
  filters: FreelancerSearchFilters,
) => {
  // Fingerprint the filters WITHOUT the page for a stable per-filter cache entry.
  const { page: _page, ...filterFingerprint } = matchQueryFromFilters(
    filters,
    jobOfferId,
    1,
  );
  return {
    queryKey: [
      TALENT_POOL_KEY,
      "match",
      companyId,
      jobOfferId,
      filterFingerprint,
    ] as const,
    queryFn: ({
      pageParam = 1,
      signal,
    }: {
      pageParam?: number;
      signal: AbortSignal;
    }) =>
      getTalentPoolMatch(
        matchQueryFromFilters(filters, jobOfferId, Number(pageParam)),
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage: MatchPoolResponse) =>
      lastPage.pagination.nextPage,
    getPreviousPageParam: (firstPage: MatchPoolResponse) =>
      firstPage.pagination.prevPage,
    placeholderData: (
      previousData: InfiniteData<MatchPoolResponse, number> | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined,
    ): InfiniteData<MatchPoolResponse, number> | undefined =>
      shouldBridgePoolMatchData(previousQuery?.queryKey, companyId, jobOfferId)
        ? previousData
        : undefined,
    retry: false,
    retryOnMount: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  };
};

// 3. Custom hook — accessor-keyed like `useInfiniteSearchFreelancers` so the query
// re-runs when the company / offer / filters change. The `enabled` gate is the
// caller's responsibility (threaded through the accessor): the pool panel mounts
// only when the tab is active + the plan gate passed, and defends with
// `!!companyId && !!jobOfferId && capabilities.enabled` so no match request ever
// fires on the Ricerca tab or under a locked plan (AC7 lazy, AC8 no-call).
export const useInfiniteTalentPoolMatch = (
  accessor: () => {
    companyId: string | undefined;
    jobOfferId: string;
    filters: FreelancerSearchFilters;
    enabled: boolean;
  },
) =>
  useInfiniteQuery(() => {
    const { companyId, jobOfferId, filters, enabled } = accessor();
    return {
      ...createMatchQueryConfig(companyId, jobOfferId, filters),
      enabled,
    };
  });
